/**
 * Sticky basket bar (#pa-sticky-cart) — floating "du har varer i kurven" bar.
 *
 * Injected on every portal page (except the cart page itself) when enabled
 * via the plugin setting (JQUERY_VARS.cartUx.stickyBasket.enabled). Purely
 * event-driven: renders from the pa:cart:count events PaCartManager
 * dispatches on every basket mutation.
 */

import './sticky_basket.scss';
import PaCartManager, { cartUx, cartLabels, cartUrl } from './cart_manager.js';

function config() {
    const sticky = cartUx().stickyBasket || {};
    const text = cartLabels();
    return {
        // Opt-in: the bar only renders when init.php says so explicitly.
        enabled: sticky.enabled === true,
        cartUrl: cartUrl(),
        clearIcon: sticky.clearIcon || '',
        barLabel: text.barLabel,
        goToCart: text.goToCart,
        clear: text.clear,
    };
}

function isCartPage( cartUrl ) {
    try {
        const cartPath = new URL( cartUrl, window.location.origin ).pathname.replace( /\/+$/, '' );
        return window.location.pathname.replace( /\/+$/, '' ) === cartPath;
    } catch ( e ) {
        return false;
    }
}

let prefersReducedMotion = false;
try {
    prefersReducedMotion = window.matchMedia && window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;
} catch ( e ) { }

// Created once in init(); update() only toggles state on these.
let bar = null;
let badge = null;
let clearButton = null;

function createBar( conf ) {
    if ( ! document.body ) {
        return false;
    }

    bar = document.createElement( 'div' );
    bar.id = 'pa-sticky-cart';
    bar.setAttribute( 'role', 'region' );
    bar.setAttribute( 'aria-live', 'polite' );

    clearButton = document.createElement( 'button' );
    clearButton.type = 'button';
    clearButton.className = 'pa-clear-cart';
    clearButton.setAttribute( 'aria-label', conf.clear );
    clearButton.title = conf.clear;
    if ( conf.clearIcon ) {
        const img = document.createElement( 'img' );
        img.alt = '';
        img.loading = 'lazy';
        img.src = conf.clearIcon;
        clearButton.appendChild( img );
    } else {
        clearButton.textContent = '×';
    }
    clearButton.addEventListener( 'click', function() {
        PaCartManager.clear( { source: 'sticky-cart-clear' } );
    } );

    badge = document.createElement( 'span' );
    badge.className = 'pa-cart-badge';
    badge.setAttribute( 'aria-hidden', 'true' );
    badge.textContent = '0';

    const label = document.createElement( 'span' );
    label.className = 'pa-label';
    label.textContent = conf.barLabel;

    const goButton = document.createElement( 'button' );
    goButton.type = 'button';
    goButton.className = 'pa-go-to-cart';
    goButton.setAttribute( 'aria-label', conf.goToCart );
    goButton.textContent = conf.goToCart;
    goButton.addEventListener( 'click', function() {
        window.location.assign( conf.cartUrl );
    } );

    bar.appendChild( clearButton );
    bar.appendChild( badge );
    bar.appendChild( label );
    bar.appendChild( goButton );
    document.body.appendChild( bar );

    return true;
}

function centerOf( rect ) {
    return { x: rect.left + ( rect.width / 2 ), y: rect.top + ( rect.height / 2 ) };
}

/**
 * Small chip that flies from the clicked button to the bar's badge.
 *
 * @param {Element} fromEl The clicked add-to-basket button.
 */
function flyChip( fromEl ) {
    if ( prefersReducedMotion || ! fromEl || ! fromEl.getBoundingClientRect || ! badge ) {
        return;
    }

    const fromRect = fromEl.getBoundingClientRect();
    const badgeRect = badge.getBoundingClientRect();
    if ( fromRect.width <= 0 || badgeRect.width <= 0 ) {
        return;
    }

    const from = centerOf( fromRect );
    const to = centerOf( badgeRect );

    const chip = document.createElement( 'div' );
    chip.className = 'pa-fly-chip';
    chip.style.left = ( from.x - 8 ) + 'px';
    chip.style.top = ( from.y - 8 ) + 'px';
    document.body.appendChild( chip );

    const dx = to.x - from.x;
    const dy = to.y - from.y;

    if ( chip.animate ) {
        const animation = chip.animate( [
            { transform: 'translate(0px, 0px) scale(1)', opacity: 1 },
            { transform: 'translate(' + dx + 'px,' + dy + 'px) scale(.55)', opacity: 0.35 },
        ], { duration: 520, easing: 'cubic-bezier(.2,.8,.25,1)', fill: 'forwards' } );
        animation.onfinish = function() {
            chip.remove();
        };
        return;
    }

    chip.style.transition = 'transform 520ms cubic-bezier(.2,.8,.25,1), opacity 520ms cubic-bezier(.2,.8,.25,1)';
    requestAnimationFrame( function() {
        chip.style.transform = 'translate(' + dx + 'px,' + dy + 'px) scale(.55)';
        chip.style.opacity = '0.35';
        setTimeout( function() {
            chip.remove();
        }, 560 );
    } );
}

let lastCount = -1;

//Sticky-specific state only. The badge TEXT is written by cart_manager's
//updateBadges (the badge carries .pa-cart-badge) — a second writer here
//would silently override its rules. Returns whether the count grew, so the
//pop animation and the fly-chip share one predicate.
function update( count ) {
    const grew = lastCount >= 0 && count > lastCount;

    bar.classList.toggle( 'is-visible', count > 0 );
    bar.setAttribute( 'aria-hidden', count > 0 ? 'false' : 'true' );
    clearButton.disabled = count <= 0;

    if ( grew ) {
        bar.classList.add( 'is-pop' );
        setTimeout( function() {
            bar.classList.remove( 'is-pop' );
        }, 380 );
    }

    lastCount = count;
    return grew;
}

let lastClickedButton = null;
let initialized = false;

export function init() {
    //Idempotent: a double init (double enqueue, optimizer plugins) would
    //append a second bar and double every listener.
    if ( initialized ) {
        return;
    }
    const conf = config();
    if ( ! conf.enabled || isCartPage( conf.cartUrl ) || ! createBar( conf ) ) {
        return;
    }
    initialized = true;

    // Remember the last clicked add-button so the fly-chip can start from it.
    document.addEventListener( 'click', function( event ) {
        if ( ! event.target || ! event.target.closest ) {
            return;
        }
        //Every product block's add-CTA — conax cards and mix tiles included
        //(.subscription-select-price was a presentation element, not a
        //button, and is gone).
        const button = event.target.closest(
            '.subscription-card-product-buy-btn button, .subscription-card-product-buy-btn a, .subscription-select-cta button, .conax-cta-button, .mix-channel'
        );
        if ( button ) {
            lastClickedButton = button;
        }
    }, true );

    window.addEventListener( 'pa:cart:count', function( event ) {
        if ( update( event.detail.count ) && lastClickedButton ) {
            flyChip( lastClickedButton );
            lastClickedButton = null;
        }
    } );

    //The bar was just created — have the badge owner (cart_manager) render
    //its text; the synchronous pa:cart:count this fires drives update() via
    //the listener above.
    PaCartManager.sync( 'sticky-basket-init' );
}

export default { init };
